import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

export default function AzureDevOpsPricingGuide() {
  return (
    <>
      <Helmet>
        <title>Azure DevOps Pricing UK 2026 — Parallel Jobs, Users &amp; Artifact Storage | AzureCalc.uk</title>
        <meta
          name="description"
          content="Azure DevOps pricing explained for UK teams. Understand parallel jobs (not agents), Microsoft-hosted vs self-hosted costs, the 5 free Basic users, and artifact storage billing — all in GBP."
        />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guides/azure-devops-pricing-uk" />
        <meta property="og:title" content="Azure DevOps Pricing UK 2026 — Parallel Jobs, Users &amp; Artifact Storage" />
        <meta property="og:url" content="https://www.azure-calc.co.uk/guides/azure-devops-pricing-uk" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <Breadcrumbs />

        <h1 className="text-2xl font-bold tracking-tight mt-4 mb-3" style={{ color: 'var(--text-primary)' }}>
          Azure DevOps Pricing UK 2026
        </h1>
        <p className="text-[14px] mb-2" style={{ color: 'var(--text-tertiary)' }}>
          Parallel jobs · User licensing · Artifact storage · Managed DevOps Pools
        </p>
        <p className="text-[12px] mb-8 italic" style={{ color: 'var(--text-tertiary)' }}>
          Built and verified by an independent Azure engineer.
        </p>

        {/* Section 1: Parallel jobs */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            1. Parallel jobs — what they actually are
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The most common Azure DevOps billing mistake is confusing parallel jobs with agents. A parallel job is not a virtual machine, not an agent, and not a runner — it is the right to execute one pipeline job at a time. A team with 50 developers and 50 self-hosted agents still only needs 1 parallel job if they never run two pipelines simultaneously. Buying additional parallel jobs when what you actually need is more agents is the billing equivalent of buying extra lanes on a road when your problem is the cars themselves. The distinction costs teams £30/month per unnecessary job.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            In practice: a startup with 8 developers committing throughout the day might trigger 15–20 pipeline runs daily. If developers commit at different times and queuing is acceptable, 1 parallel job handles this fine. The queue clears. If you need guaranteed simultaneous runs — for example, a monorepo where every PR triggers frontend, backend, and infrastructure pipelines at the same time — you need 3 parallel jobs.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            The free tier gives you 1 Microsoft-hosted parallel job with 1,800 minutes per month (about 30 hours). For a 10-minute build, that is 180 runs — more than enough for a team of 8 running one build per commit. The free tier resets monthly.
          </p>
        </section>

        {/* Section 2: MS-hosted vs self-hosted */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            2. Microsoft-hosted vs self-hosted — cost comparison
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Microsoft-hosted agents come ready with build tools pre-installed. You pay nothing for the first 1,800 minutes/month, then £30.19/month for each additional parallel job. Each additional job adds unlimited minutes — you are not buying minutes, you are buying capacity to run another pipeline simultaneously.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Self-hosted agents run on your own infrastructure — an Azure VM, an on-premises server, or a container. The first self-hosted parallel job is free with unlimited minutes. Each additional self-hosted parallel job is £11.32/month. Teams switch to self-hosted when builds exceed the 1,800-minute free tier, when they need custom software or GPU access, or when they want to avoid per-minute charges entirely.
          </p>

          <div
            className="rounded-lg overflow-hidden text-[13px] mb-4"
            style={{ border: '1px solid var(--outline-variant)' }}
          >
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Type</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Free tier</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Additional job</th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'var(--surface-container)' }}>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Microsoft-hosted</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-secondary)' }}>1 job, 1,800 min/month</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>£30.19/month</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Self-hosted</td>
                  <td className="text-right px-4 py-2 font-mono" style={{ color: 'var(--text-secondary)' }}>1 job, unlimited minutes</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>£11.32/month</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Self-hosted parallel jobs are cheaper per job because you are providing the compute. If you are already running an Azure VM for other purposes, adding a self-hosted agent costs only the additional parallel job fee — not the VM.
          </p>
        </section>

        {/* Section 3: User licensing */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            3. User licensing — the 5 free users
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The first 5 Basic users are always free, regardless of organisation size. A team of 100 still gets 5 free. Stakeholder access — read-only access to boards and backlogs — is always free and unlimited. Only engineers who need to create work items, run pipelines, access repos, or write test cases need a paid seat.
          </p>

          <div
            className="rounded-lg overflow-hidden text-[13px] mb-4"
            style={{ border: '1px solid var(--outline-variant)' }}
          >
            <table className="w-full">
              <thead style={{ backgroundColor: 'var(--surface-container-low)' }}>
                <tr>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Access level</th>
                  <th className="text-right px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Rate</th>
                  <th className="text-left px-4 py-2 font-semibold" style={{ color: 'var(--text-secondary)' }}>Includes</th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: 'var(--surface-container)' }}>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Stakeholder</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>Free</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-tertiary)' }}>Read-only boards, backlogs</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Basic (first 5)</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>Free</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-tertiary)' }}>Pipelines, Boards, Repos, basic Test Plans</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Basic (6th+)</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>£4.53/user/month</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-tertiary)' }}>Pipelines, Boards, Repos, basic Test Plans</td>
                </tr>
                <tr style={{ borderTop: '1px solid var(--outline-variant)' }}>
                  <td className="px-4 py-2" style={{ color: 'var(--text-secondary)' }}>Basic + Test Plans</td>
                  <td className="text-right px-4 py-2 font-mono font-medium" style={{ color: 'var(--text-primary)' }}>£39.25/user/month</td>
                  <td className="px-4 py-2" style={{ color: 'var(--text-tertiary)' }}>Everything + full Test Plans</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
            Test Plans is only worth the premium for dedicated QA engineers who use the full test case management, parameterised testing, and manual exploratory testing features. Most teams assign it to 1–3 people, not the whole org.
          </p>
        </section>

        {/* Section 4: Artifact storage */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            4. Artifact storage — what counts and what's free
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            The first 2 GB of artifact storage is free. Beyond that, the cost is £1.51/GB/month. Artifact storage covers everything published to Azure Artifacts: NuGet packages, npm packages, Maven artefacts, Python wheels, and universal packages. Pipeline artefacts (build outputs uploaded via publish pipeline artefact tasks) also count toward this quota.
          </p>
          <p className="text-[14px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            The 2 GB free tier goes quickly if you publish binaries or large build outputs. Retention policies on pipeline artefacts are the most effective way to control storage growth — automatically deleting artefacts after 30 days for feature branches and 90 days for main keeps the bill predictable.
          </p>
        </section>

        {/* Section 5: Managed DevOps Pools */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            5. Managed DevOps Pools
          </h2>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            Managed DevOps Pools (released in 2025) are Microsoft-managed self-hosted agent pools backed by Azure VMs. You choose the VM size; Microsoft handles provisioning, scaling, and lifecycle. The cost is the VM rate for the time agents are running, plus the self-hosted parallel job fee for any jobs beyond the first.
          </p>
          <p className="text-[14px] leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            For a D2s v5 agent running 8 hours/day on weekdays (about 176 hours/month), the VM cost is approximately £13/month. This is significantly cheaper than a Microsoft-hosted additional parallel job (£30.19) and gives you full control over the build environment.
          </p>
          <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
            Use the <Link to="/virtual-machines" className="underline" style={{ color: '#0078d4' }}>Virtual Machines calculator</Link> to estimate the VM cost for your chosen agent size, then add it to the DevOps parallel job cost from the calculator below.
          </p>
        </section>

        {/* Section 6: Practical examples */}
        <section className="mb-10">
          <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            6. Practical examples
          </h2>

          <div className="flex flex-col gap-4">
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
            >
              <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Small team — 5 developers</p>
              <ul className="text-[13px] leading-relaxed list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
                <li>1 Microsoft-hosted parallel job (free)</li>
                <li>5 Basic users (all free)</li>
                <li>2 GB artifact storage (free)</li>
                <li className="font-semibold mt-1">Monthly total: £0</li>
              </ul>
            </div>

            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
            >
              <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Medium team — 20 developers, active CI</p>
              <ul className="text-[13px] leading-relaxed list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
                <li>2 Microsoft-hosted parallel jobs (1 free + 1 at £30.19)</li>
                <li>20 Basic users (5 free + 15 at £4.53)</li>
                <li>2 Test Plan users (£39.25 each)</li>
                <li>5 GB artifact storage (3 GB billable at £1.51)</li>
                <li className="font-semibold mt-1">Monthly total: £30.19 + £67.95 + £78.50 + £4.53 = £181.17</li>
              </ul>
            </div>

            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
            >
              <p className="text-[13px] font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Enterprise — monorepo, simultaneous pipelines</p>
              <ul className="text-[13px] leading-relaxed list-disc list-inside" style={{ color: 'var(--text-secondary)' }}>
                <li>3 Microsoft-hosted parallel jobs (1 free + 2 at £30.19)</li>
                <li>50 Basic users (5 free + 45 at £4.53)</li>
                <li>5 Test Plan users (£39.25 each)</li>
                <li>10 GB artifact storage (8 GB billable at £1.51)</li>
                <li className="font-semibold mt-1">Monthly total: £60.38 + £203.85 + £196.25 + £12.08 = £472.56</li>
              </ul>
            </div>
          </div>
        </section>

        <div className="mb-10">
          <CalculatorEmbed service="azure-devops" />
        </div>

        <div
          className="mt-8 pt-6 text-[13px]"
          style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}
        >
          <p>
            Use the{' '}
            <Link to="/azure-devops" className="underline" style={{ color: '#0078d4' }}>
              Azure DevOps Calculator →
            </Link>{' '}
            to estimate your costs.
          </p>
        </div>
      </div>
    </>
  )
}
